import { conversionFactors } from "../conversionFactors";
import { formatDate } from "../formatters";

function getConversions(detail, selectedUnit) {
  const pondLengthUnit = detail.LengthUnitName?.toLowerCase() || "inch";
  const pondWeightUnit = detail.WeightUnitName?.toLowerCase() || "pound";
  const lengthConversion =
    conversionFactors.length[pondLengthUnit][selectedUnit.value];
  const weightConversion =
    conversionFactors.weight[pondWeightUnit][selectedUnit.value];
  const convertLength = (value) => parseFloat(value || 0) * lengthConversion;
  const convertWeight = (value) => parseFloat(value || 0) * weightConversion;
  return { convertLength, convertWeight, pondLengthUnit };
}

export function extractWrVsLength(detail, ctx) {
  const { selectedFish, selectedUnit } = ctx;
  const rows = [];
  const { convertLength } = getConversions(detail, selectedUnit);
  if (detail.SamplingData) {
    detail.SamplingData.forEach((fish) => {
      if (
        selectedFish.length === 0 ||
        selectedFish.some((f) => f.label === fish.FishTypeName)
      ) {
        const normalizedTag = (fish.TagNumber || "").toString().trim();
        rows.push({
          length: convertLength(fish.Length),
          relativeWeight: parseFloat(fish.Wr || 0),
          species: fish.FishTypeName || "Unknown",
          tagNumber: normalizedTag || null,
          isTagged: !!normalizedTag,
        });
      }
    });
  }
  return rows;
}

export function extractWeightVsLength(detail, ctx) {
  const { selectedFish, selectedUnit } = ctx;
  const rows = [];
  const { convertLength, convertWeight } = getConversions(detail, selectedUnit);
  if (detail.SamplingData) {
    detail.SamplingData.forEach((fish) => {
      if (
        (selectedFish.length === 0 ||
          selectedFish.some((f) => f.label === fish.FishTypeName)) &&
        fish.Length > 0 &&
        fish.Weight > 0
      ) {
        const normalizedTag = (fish.TagNumber || "").toString().trim();
        const ft = fish.FishTypeName || "Unknown";
        rows.push({
          length: convertLength(fish.Length),
          actualWeight: convertWeight(fish.Weight),
          standardWeight: convertWeight(fish.Ws),
          species: ft,
          fishTypeName: ft,
          tagNumber: normalizedTag || null,
          isTagged: !!normalizedTag,
        });
      }
    });
  }
  return rows;
}

function sampleDateKeyFromDetail(date) {
  return formatDate(date) || "unknown";
}

export function extractWeightVsLengthByDate(detail, ctx) {
  const { selectedFish, selectedUnit, formatDateTime } = ctx;
  const rows = [];
  const { convertLength, convertWeight } = getConversions(detail, selectedUnit);
  const sampleDateKey = sampleDateKeyFromDetail(detail.Date);
  const sampleDateLabel = formatDateTime
    ? formatDateTime(detail.Date)
    : String(detail.Date ?? "N/A");

  if (detail.SamplingData) {
    detail.SamplingData.forEach((fish) => {
      if (
        (selectedFish.length === 0 ||
          selectedFish.some((f) => f.label === fish.FishTypeName)) &&
        fish.Length > 0 &&
        fish.Weight > 0
      ) {
        const normalizedTag = (fish.TagNumber || "").toString().trim();
        rows.push({
          length: convertLength(fish.Length),
          actualWeight: convertWeight(fish.Weight),
          standardWeight: convertWeight(fish.Ws),
          species: fish.FishTypeName || "Unknown",
          tagNumber: normalizedTag || null,
          isTagged: !!normalizedTag,
          sampleDateKey,
          sampleDateLabel,
        });
      }
    });
  }
  return rows;
}

export function extractAvgWrVsDate(detail, ctx) {
  const { selectedFish, formatDateTime } = ctx;
  const rows = [];
  if (detail.FishType) {
    detail.FishType.forEach((summary) => {
      if (
        selectedFish.length === 0 ||
        selectedFish.some((f) => f.label === summary.FishTypeName)
      ) {
        const date = formatDateTime(detail.Date);
        rows.push({
          date,
          averageWr: parseFloat((summary.AverageWr || 0).toFixed(2)),
          stdev: parseFloat((summary.StandardDeviationWr || 0).toFixed(2)),
          count: summary.NumberOfFishes || 0,
          species: summary.FishTypeName || "Unknown",
        });
      }
    });
  }
  return rows;
}

/** One row per tagged fish capture: Wr vs sampling session date (not pooled by species/day). */
export function extractTaggedWrVsDate(detail, ctx) {
  const { selectedFish, formatDateTime } = ctx;
  const rows = [];
  const date = formatDateTime ? formatDateTime(detail.Date) : String(detail.Date ?? "N/A");

  if (!detail.SamplingData) return rows;

  detail.SamplingData.forEach((fish) => {
    if (
      selectedFish.length > 0 &&
      !selectedFish.some((f) => f.label === fish.FishTypeName)
    ) {
      return;
    }
    const normalizedTag = (fish.TagNumber || "").toString().trim();
    if (!normalizedTag) return;

    const wr = parseFloat(fish.Wr);
    if (!Number.isFinite(wr)) return;

    const species = fish.FishTypeName || "Unknown";
    rows.push({
      date,
      wr,
      species,
      tagNumber: normalizedTag,
      isTagged: true,
      seriesKey: `${species} · Tag ${normalizedTag}`,
    });
  });

  return rows;
}

export function extractStandardVsActual(detail, ctx) {
  const { selectedFish, selectedUnit } = ctx;
  const rows = [];
  const { convertLength, convertWeight } = getConversions(detail, selectedUnit);
  if (detail.SamplingData) {
    detail.SamplingData.forEach((fish) => {
      if (
        selectedFish.length === 0 ||
        selectedFish.some((f) => f.label === fish.FishTypeName)
      ) {
        const normalizedTag = (fish.TagNumber || "").toString().trim();
        rows.push({
          standardWeight: convertWeight(fish.Ws),
          actualWeight: convertWeight(fish.Weight),
          species: fish.FishTypeName || "Unknown",
          length: convertLength(fish.Length),
          tagNumber: normalizedTag || null,
          isTagged: !!normalizedTag,
        });
      }
    });
  }
  return rows;
}

export function extractNumberFrequency(detail, ctx) {
  const { selectedFish, selectedUnit } = ctx;
  const rows = [];
  if (detail.SamplingData) {
    detail.SamplingData.forEach((fish) => {
      if (
        selectedFish.length === 0 ||
        selectedFish.some((f) => f.label === fish.FishTypeName)
      ) {
        const originalLength = parseFloat(fish.Length || 0);
        const pondLengthUnit = detail.LengthUnitName?.toLowerCase() || "inch";
        const lengthConversion =
          conversionFactors.length[pondLengthUnit][selectedUnit.value];
        const convertedLength = originalLength * lengthConversion;
        const normalizedTag = (fish.TagNumber || "").toString().trim();

        const status = fish.Status || fish.status || fish.FishStatus || "Unknown";

        rows.push({
          length: convertedLength,
          originalLength: originalLength,
          relativeWeight: parseFloat(fish.Wr || 0),
          species: fish.FishTypeName || "Unknown",
          status: status,
          tagNumber: normalizedTag || null,
          isTagged: !!normalizedTag,
        });
      }
    });
  }
  return rows;
}

export function extractNumberFrequencyVsBin(detail, ctx) {
  const { selectedFish, selectedUnit } = ctx;
  const rows = [];
  if (detail.SamplingData) {
    detail.SamplingData.forEach((fish) => {
      if (
        selectedFish.length === 0 ||
        selectedFish.some((f) => f.label === fish.FishTypeName)
      ) {
        const originalLength = parseFloat(fish.Length || 0);
        const pondLengthUnit = detail.LengthUnitName?.toLowerCase() || "inch";
        const lengthConversion =
          conversionFactors.length[pondLengthUnit][selectedUnit.value];
        const convertedLength = originalLength * lengthConversion;
        const status = fish.Status || fish.status || fish.FishStatus || "Unknown";
        const normalizedTag = (fish.TagNumber || "").toString().trim();

        rows.push({
          length: convertedLength,
          originalLength: originalLength,
          relativeWeight: parseFloat(fish.Wr || 0),
          species: fish.FishTypeName || "Unknown",
          status: status,
          tagNumber: normalizedTag || null,
          isTagged: !!normalizedTag,
        });
      }
    });
  }
  return rows;
}

export function extractNumberFrequencyVsBinByDate(detail, ctx) {
  const { selectedFish, selectedUnit, formatDateTime } = ctx;
  const rows = [];
  const sampleDateKey = sampleDateKeyFromDetail(detail.Date);
  const sampleDateLabel = formatDateTime
    ? formatDateTime(detail.Date)
    : String(detail.Date ?? "N/A");

  if (detail.SamplingData) {
    detail.SamplingData.forEach((fish) => {
      if (
        selectedFish.length === 0 ||
        selectedFish.some((f) => f.label === fish.FishTypeName)
      ) {
        const originalLength = parseFloat(fish.Length || 0);
        const pondLengthUnit = detail.LengthUnitName?.toLowerCase() || "inch";
        const lengthConversion =
          conversionFactors.length[pondLengthUnit][selectedUnit.value];
        const convertedLength = originalLength * lengthConversion;
        const status = fish.Status || fish.status || fish.FishStatus || "Unknown";
        const normalizedTag = (fish.TagNumber || "").toString().trim();

        rows.push({
          length: convertedLength,
          originalLength: originalLength,
          relativeWeight: parseFloat(fish.Wr || 0),
          species: fish.FishTypeName || "Unknown",
          status: status,
          tagNumber: normalizedTag || null,
          isTagged: !!normalizedTag,
          sampleDateKey,
          sampleDateLabel,
        });
      }
    });
  }
  return rows;
}

const EXTRACTORS = {
  "wr-vs-length": extractWrVsLength,
  "weight-vs-length": extractWeightVsLength,
  "weight-vs-length-by-date": extractWeightVsLengthByDate,
  "avg-wr-vs-date": extractAvgWrVsDate,
  "tagged-wr-vs-date": extractTaggedWrVsDate,
  "standard-vs-actual": extractStandardVsActual,
  "number-frequency": extractNumberFrequency,
  "number-frequency-vs-bin": extractNumberFrequencyVsBin,
  "number-frequency-vs-bin-by-date": extractNumberFrequencyVsBinByDate,
};

export function extractRowsForGraphType(graphType, detail, ctx) {
  const fn = EXTRACTORS[graphType];
  return fn ? fn(detail, ctx) : [];
}
